// Named import, not default: `Decimal` is a merged class + namespace declaration, and only the
// named binding carries both the type and the static members (`clone`, `ROUND_HALF_EVEN`).
import { Decimal } from 'decimal.js';
import { decimalPlacesFor, isCurrency } from './currency.js';
import { err, ok } from './result.js';
/**
 * Money — the only representation of a monetary amount permitted anywhere in this system.
 *
 * Rules this type exists to enforce (see docs/plan/06-risks.md R3):
 *  - Never a JavaScript `number`. Binary floating point cannot represent 0.1 exactly, and a
 *    ledger that drifts by a satang is a ledger nobody trusts.
 *  - Never mix currencies implicitly. Arithmetic across currencies is a type-level failure.
 *  - Equality is exact decimal equality, because SAD §6 requires the slip amount to match the
 *    reserved amount *exactly* before auto-approval.
 *
 * Wire format is a decimal string (SAD §8: "Money is decimal strings + currency, never float").
 * Storage format is `NUMERIC(20,4)` (SAD §12).
 */
/** Storage scale, matching `NUMERIC(20,4)` in the database. */
const STORAGE_SCALE = 4;
/** Maximum total digits, matching `NUMERIC(20,4)`. */
const STORAGE_PRECISION = 20;
/** Raised when arithmetic is attempted across two different currencies. */
export class CurrencyMismatchError extends Error {
    left;
    right;
    constructor(left, right) {
        super(`Cannot operate on ${left} and ${right} — currencies must match`);
        this.left = left;
        this.right = right;
        this.name = 'CurrencyMismatchError';
    }
}
/**
 * Decimal.js configured for financial use: no exponential notation on toString, and enough
 * precision that intermediate results never silently round.
 */
const Money$ = Decimal.clone({
    precision: 34,
    toExpNeg: -9e15,
    toExpPos: 9e15,
    rounding: Decimal.ROUND_HALF_EVEN,
});
/** A strictly non-negative monetary amount in a single currency. */
export class Money {
    amount;
    currency;
    constructor(amount, currency) {
        this.amount = amount;
        this.currency = currency;
    }
    /**
     * Construct from a decimal string, rejecting anything that is not an exact, in-range,
     * correctly-scaled amount.
     *
     * Deliberately does not accept `number` — see the class doc.
     */
    static from(value, currency) {
        if (!isCurrency(currency)) {
            return err({ kind: 'UNSUPPORTED_CURRENCY', value: currency });
        }
        if (typeof value !== 'string' || !/^\d+(\.\d+)?$/.test(value.trim())) {
            return err({ kind: 'INVALID_FORMAT', value: String(value) });
        }
        const trimmed = value.trim();
        const allowed = decimalPlacesFor(currency);
        const fraction = trimmed.split('.')[1] ?? '';
        if (fraction.length > allowed) {
            return err({ kind: 'TOO_MANY_DECIMAL_PLACES', value: trimmed, allowed });
        }
        let decimal;
        try {
            decimal = new Money$(trimmed);
        }
        catch {
            return err({ kind: 'INVALID_FORMAT', value: trimmed });
        }
        if (!decimal.isFinite())
            return err({ kind: 'INVALID_FORMAT', value: trimmed });
        if (decimal.isNegative())
            return err({ kind: 'NEGATIVE', value: trimmed });
        if (decimal.precision(true) > STORAGE_PRECISION) {
            return err({ kind: 'OUT_OF_RANGE', value: trimmed });
        }
        return ok(new Money(decimal, currency));
    }
    /**
     * Construct from a value already known to be valid (a database read, or a literal in a test).
     *
     * @throws if the value is not valid — this is a programmer error, not a business failure.
     */
    static of(value, currency) {
        const result = Money.from(value, currency);
        if (!result.ok) {
            throw new Error(`Money.of received an invalid amount: ${JSON.stringify(result.error)}`);
        }
        return result.value;
    }
    /** A zero amount in the given currency. */
    static zero(currency) {
        return new Money(new Money$(0), currency);
    }
    /** Exact decimal equality, including currency. Never approximate. */
    equals(other) {
        return this.currency === other.currency && this.amount.equals(other.amount);
    }
    /** Negative if less, zero if equal, positive if greater. */
    compare(other) {
        this.assertSameCurrency(other);
        return this.amount.comparedTo(other.amount);
    }
    /** True when this amount is strictly greater than `other`. */
    isGreaterThan(other) {
        return this.compare(other) > 0;
    }
    /** True when this amount is strictly less than `other`. */
    isLessThan(other) {
        return this.compare(other) < 0;
    }
    /** True when this amount is exactly zero. */
    isZero() {
        return this.amount.isZero();
    }
    /** Sum of two amounts in the same currency. */
    add(other) {
        this.assertSameCurrency(other);
        return new Money(this.amount.plus(other.amount), this.currency);
    }
    /**
     * Difference of two amounts in the same currency.
     *
     * @throws if the result would be negative — `Money` is non-negative by construction, and a
     *   negative balance in this system always indicates a bug rather than a legitimate state.
     */
    subtract(other) {
        this.assertSameCurrency(other);
        const result = this.amount.minus(other.amount);
        if (result.isNegative()) {
            throw new RangeError(`Money.subtract would produce a negative amount: ${this.toString()} - ${other.toString()}`);
        }
        return new Money(result, this.currency);
    }
    /**
     * This amount scaled by the rational factor `numerator / denominator`, rounded **down** to the
     * storage scale.
     *
     * Exists for policy bounds *derived from* an amount — the split ratio ceiling — never for
     * ledger arithmetic: nothing that posts to a ledger may round, which is why the rounding mode
     * is pinned here rather than left to configuration. Rounding down keeps a derived ceiling
     * conservative — the bound never admits a value the exact ratio would refuse.
     *
     * @throws RangeError unless both factors are positive integers. A fractional or zero factor
     *   here is a programmer error, not a business input — business ratios arrive as validated
     *   integer percentages.
     */
    scaleBy(numerator, denominator) {
        if (!Number.isInteger(numerator) ||
            numerator <= 0 ||
            !Number.isInteger(denominator) ||
            denominator <= 0) {
            throw new RangeError(`Money.scaleBy requires positive integer factors, got ${numerator}/${denominator}`);
        }
        const scaled = this.amount
            .times(numerator)
            .dividedBy(denominator)
            .toDecimalPlaces(STORAGE_SCALE, Decimal.ROUND_DOWN);
        return new Money(scaled, this.currency);
    }
    /**
     * Wire representation: a fixed-scale decimal string in the currency's minor units.
     *
     * @example Money.of('500', 'THB').toString() === '500.00'
     */
    toString() {
        return this.amount.toFixed(decimalPlacesFor(this.currency));
    }
    /** Database representation, at the full `NUMERIC(20,4)` storage scale. */
    toStorageString() {
        return this.amount.toFixed(STORAGE_SCALE);
    }
    /** JSON representation used across the API (SAD §8). */
    toJSON() {
        return { value: this.toString(), currency: this.currency };
    }
    assertSameCurrency(other) {
        if (this.currency !== other.currency) {
            throw new CurrencyMismatchError(this.currency, other.currency);
        }
    }
}
//# sourceMappingURL=money.js.map